/**
 * Año y mes, por separado: el vocabulario de las VINCULACIONES.
 *
 * La vinculación deja de llevar un periodo compuesto `AAAA-MM` y pasa a llevar
 * los dos campos sueltos, así que la interfaz también: dos columnas que
 * ordenan como números y ningún sitio donde se pegue una cadena.
 *
 * No confundir con `periods.ts`, que es el `AAAA-MM` del panel y de las
 * MECÁNICAS (§7.3.5), y que este cambio no tocó. Son dos vocabularios y
 * conviven: `fromPeriod` es el único sitio donde se pasa de uno al otro, que
 * es como se traduce en la frontera de un contexto.
 *
 * Los nombres de los meses van escritos y no salen de `Intl`: formatear un mes
 * obliga a construir una fecha, y una fecha construida en UTC y leída desde
 * Ecuador cae en el mes anterior. Doce palabras fijas no tienen esa trampa.
 */
export interface MonthOption {
  readonly value: number;
  readonly label: string;
}

export const MONTHS: readonly MonthOption[] = [
  { value: 1, label: "Enero" },
  { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" },
  { value: 6, label: "Junio" },
  { value: 7, label: "Julio" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" },
  { value: 12, label: "Diciembre" },
];

/**
 * El nombre del mes.
 *
 * Un número fuera de los doce se enseña tal cual en vez de dejar la celda
 * vacía: si el backend manda algo que no es un mes, verlo es mejor que no
 * verlo.
 */
export const monthLabel = (month: number): string =>
  MONTHS.find((option) => option.value === month)?.label ?? String(month);

/**
 * El mes del panel, en el vocabulario de las vinculaciones.
 *
 * La cabecera del panel elige UN mes y lo dice como `AAAA-MM`, porque eso es
 * lo que entienden las mecánicas y los lotes. El padrón necesita los dos
 * números, así que aquí se parte --y solo aquí--.
 *
 * El mes puede no venir (`2026`), y entonces lo que se mira es el año entero:
 * sale el año sin mes, que es exactamente lo que `find_participants` entiende
 * por "todos los meses de 2026". Lo que no tenga ninguna de las dos formas
 * devuelve `undefined`: la URL la puede escribir cualquiera.
 */
const PERIOD_PATTERN = /^(\d{4})(?:-(\d{2}))?$/;

export const fromPeriod = (
  period: string,
): { readonly year: number; readonly month?: number } | undefined => {
  const match = PERIOD_PATTERN.exec(period.trim());
  if (match?.[1] === undefined) return undefined;

  const year = Number(match[1]);
  if (match[2] === undefined) return { year };

  const month = Number(match[2]);
  if (month < FIRST_MONTH || month > LAST_MONTH) return undefined;

  return { year, month };
};

const FIRST_MONTH = 1;
const LAST_MONTH = 12;

/**
 * El año que el usuario ha escrito, cuando ya es un año.
 *
 * Mientras teclea "20" no se consulta por el año 20: hasta que no hay cuatro
 * cifras esto devuelve `undefined` y el filtro se comporta como si no
 * estuviera puesto.
 */
const YEAR_PATTERN = /^\d{4}$/;

export const parseYear = (text: string): number | undefined => {
  const trimmed = text.trim();
  return YEAR_PATTERN.test(trimmed) ? Number(trimmed) : undefined;
};
